import { Box, Grid2, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "./MyRewardsPage.module.scss";
import RewardSummaryCard from "../../../features/reward-summary-card/RewardSummaryCard";
import RewardTierCard from "../../../features/reward-tier-card/RewardTierCard";
import PointsEarnedSection from "../../../features/points-earned-section/PointsEarnedSection";
import RewardsPageFooterSection from "../../../features/rewards-page-footer-section/RewardsPageFooterSection";
import { useAppSelector } from "../../../store/hooks";
import { useGetRewardProgressWidgetQuery } from "./services/rewardsProgressWidgetApi";
import { SKY_BLUE } from "../../../utility/constants/common-constants";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const MyRewardsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const {
    patientProfileInfo: {
      patientProfileId,
      preferredLanguage: patientPreferredLang,
    },
  } = useAppSelector((state) => state.patientProfileDetails);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.state]);

  const {
    data: rewardData,
    isLoading,
    error,
  } = useGetRewardProgressWidgetQuery({
    patientProfileId,
    patientPreferredLang,
  });

  const summaryCardData = useMemo(() => {
    const currentTierClassificationId = rewardData?.currentTierClassificationId;
    const progress = rewardData?.rewardProgress ?? [];
    // Filter out null or zero values
    const rangeMilestones = progress
      .map((r) => r.rangeFrom)
      .filter((p): p is number => p != null && p !== 0);
    const bonus = rewardData?.optinBonus;
    const milestonesSet = new Set<number>([
      ...rangeMilestones,
      ...(typeof bonus === "number" && bonus !== 0 ? [bonus] : []),
    ]);
    const milestones = Array.from(milestonesSet).sort((a, b) => a - b);
    const maxValue = milestones.at(-1) ?? 4000;
    const longDescription = progress.find(
      (r) => r.tierClassificationId === currentTierClassificationId
    )?.longDescription;

    return {
      currentPoints: rewardData?.totalEarnedRewardPoints ?? 0,
      optinBonus: bonus,
      currentTierDisplayName: rewardData?.currentTierDisplayName,
      currentTierClassificationId,
      nextTierMessage: rewardData?.nextTierMessage,
      milestones,
      maxValue,
      longDescription,
    };
  }, [rewardData]);

  const tierCardData = rewardData?.rewardProgress ?? [];

  return (
    <BackgroundLayout
      variant={"default"}
      height={{ xs: "13rem", sm: "14rem", md: "29rem" }}
      backgroundColor={SKY_BLUE}
      showFooter={true}
    >
      <Box className={styles.mainLayout}>
        <Box className={styles.pageTitleContainer}>
          <Typography variant={isMobile ? "h2" : "h1"}>
            {t("rewardsWidget.title")}
          </Typography>
        </Box>

        <Grid2 container spacing={2.5} className={styles.gridContainer}>
          <Grid2 size={{ xs: 12, sm: 8, md: 6 }} m={{ sm: "0 auto" }}>
            <RewardSummaryCard
              data={{
                ...summaryCardData,
              }}
              isLoading={isLoading}
              error={error}
            />
          </Grid2>

          <Grid2
            size={{ xs: 12, sm: 8, md: 6 }}
            p={{ xs: "0 1rem", md: "0" }}
            m={{ sm: "0 auto" }}
          >
            <RewardTierCard
              data={tierCardData ?? []}
              optinBonus={rewardData?.optinBonus}
              isLoading={isLoading}
              error={error}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={3} className={styles.pointsEarnedContainer}>
          <Grid2 size={12}>
            <PointsEarnedSection />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={3} className={styles.gridContainer}>
          <Grid2 size={12} p={{ xs: "0 1rem", md: "1rem" }}>
            <RewardsPageFooterSection />
          </Grid2>
        </Grid2>
      </Box>
    </BackgroundLayout>
  );
};

export default MyRewardsPage;
